import WebFont from 'webfontloader'
import $ from 'jquery'
import gsap from 'gsap'
import DataManager from '../../data/DataManager'
import RankingManager from '../../ranking/RankingManager'
import ShareUtil from '../../util/ShareUtil'
import TimeUtil from '../../util/TimeUtil'
import Fader from '../../common/Fader'
import GameOverView from '../../gameover/GameOverView'
import RankingView from '../../ranking/RankingView'

import * as PIXI from 'pixi.js'
import { RGBSplitFilter } from 'pixi-filters'

import AsseetsManager from '../../pixi/AssetsManager'
import MyApplication from '../../pixi/MyApplication'
import TopView from './top/TopView'
import GameView from './game/GameView'
import MathUtil from '../../util/MathUtil'

/**
 * Mainクラス
 */
class App {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //コンフィグデータ
  private _config: any

  //PIXIアプリケーション
  private _app: PIXI.Application

  //TOP画面
  private _top: TopView

  //ゲーム画面
  private _game: GameView

  //ゲームオーバー画面
  private _gameOverView: GameOverView

  //ランキング画面
  private _rankingView: RankingView

  //エフェクト関連
  private _rgb: RGBSplitFilter
  public rgbVal: number
  public rgbRot: number

  // ---------------------------------------------------------------------------------------------------------
  // --	App
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  constructor() {
    console.log('App:::solitia')

    //DBの設定
    DataManager.instance.setData(
      'APPLICATION_KEY',
      '1639606783e14fdf0e99fce7b2d464583e0f282e8c1d9b5ffd9620b5e758630e'
    )
    DataManager.instance.setData(
      'CLIENT_KEY',
      'b125050987b5683a2c96b5c70a0fe7de4dfac2c1ff177c9b6b8ef9c459ac0c2a'
    )

    //フォントの読み込みへ
    window.onload = this.loadFonts.bind(this)
  }

  /**
   * フォント読み込み
   */
  loadFonts() {
    WebFont.load({
      custom: {
        families: [
          'PixelMplus12-Regular',
          'PixelMplus12-Bold',
          'PixelMplus10-Regular',
          'PixelMplus10-Bold',
          'BIT',
        ],
      },
      loading: () => {},
      active: this.loadConfig.bind(this),
      inactive: () => {},
    })
  }

  /**
   * Config読み込み
   */
  loadConfig() {
    $.getJSON('config.json')
      .done((json) => {
        this._config = json
        DataManager.instance.setData('config', this._config)
        // this.setup()
        this.loadAssets()
      })
      .fail(() => {
        throw new Error('config.jsonの読み込みに失敗しました')
      })
      .always(() => {})
  }

  /**
   * アセット読み込み
   */
  async loadAssets() {
    const bundle = this._config.BUNDLE
    await AsseetsManager.instance.loadBundle(bundle.name, bundle.assets)
    this.setup()
  }

  /**
   * セットアップ
   */
  async setup() {
    //PIXIアプリケーション参照
    this._app = MyApplication.app
    this._app.renderer.background.color = 0x4a660d

    //トップ画面
    this._top = new TopView()
    this._top.emitter.on('START', this.onGameStart.bind(this))
    this._app.stage.addChild(this._top)

    //ゲーム画面
    this._game = new GameView()
    this._game.emitter.on('MISS', this.missEffect.bind(this))
    this._game.emitter.on('HENSIN', this.hensinEffect.bind(this))
    this._app.stage.addChild(this._game)

    //エフェクトの初期化
    this.initFilter()

    //リサイズハンドリング
    window.onresize = this.onResize.bind(this)
    this.onResize()

    //スタート
    this.start()
  }

  /**
   * スタート
   */
  start() {
    //フェードイン
    Fader.instance.fadeIn()

    //トップ画面開く
    this._top.open()
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	GAME
  // ---------------------------------------------------------------------------------------------------------

  /**
   * ゲームスタート
   */
  async onGameStart() {
    //フェードイン
    await Fader.instance.fadeOut()

    //待ち
    await TimeUtil.wait(500)

    //トップ破棄
    this._top.destroy()
    this._top = null

    //GAME開く
    this._game.open()

    //フェードイン
    await Fader.instance.fadeIn()

    //GAMEスタート
    this._game.start()
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	エフェクト関連
  // ---------------------------------------------------------------------------------------------------------

  /**
   * フィルターの初期化
   */
  initFilter() {
    this._rgb = new RGBSplitFilter()
    this._app.stage.filters = [this._rgb]

    this.rgbVal = 0
    this.rgbRot = 0
    this.updateRGB()
  }

  missEffect() {
    gsap.killTweensOf(this)
    gsap.set(this, { rgbVal: 30 })
    // gsap.set(this, { rgbVal: 60 })
    this.updateRGB()

    gsap.to(this, {
      // duration: 2.4,
      duration: 1.2,
      rgbVal: 0,
      rgbRot: this.rgbRot + 720,
      ease: 'elastic.out(1, 0.3)',
      onUpdate: this.updateRGB.bind(this),
    })
  }

  hensinEffect() {
    gsap.killTweensOf(this)
    gsap.set(this, { rgbVal: 60 })
    this.updateRGB()

    gsap.to(this, {
      duration: 2.4,
      rgbVal: 0,
      rgbRot: this.rgbRot + 720,
      ease: 'elastic.out(1, 0.3)',
      onUpdate: this.updateRGB.bind(this),
    })
  }

  updateRGB() {
    const redRadian = MathUtil.degreeToRadian(this.rgbRot + 180)
    this._rgb.red = [Math.cos(redRadian) * this.rgbVal, Math.sin(redRadian) * this.rgbVal]

    const greenRadian = MathUtil.degreeToRadian(this.rgbRot + 90)
    this._rgb.green = [Math.cos(greenRadian) * this.rgbVal, Math.sin(greenRadian) * this.rgbVal]

    const blueRadian = MathUtil.degreeToRadian(this.rgbRot - 45)
    this._rgb.blue = [Math.cos(blueRadian) * this.rgbVal, Math.sin(blueRadian) * this.rgbVal]
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	TWEET
  // ---------------------------------------------------------------------------------------------------------

  /**
   * ツイート
   */
  onTweet(score: number) {
    ShareUtil.tweet(
      '【THE FIRST カニテング】で、鼻をのばして' + String(score) + '匹のカニをやっつけたぞ！'
    )
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	リサイズ
  // ---------------------------------------------------------------------------------------------------------

  /**
   * リサイズハンドリング
   */
  onResize() {
    //PIXIアプリケーションのリサイズ
    MyApplication.resize()

    //TOP画面リサイズ
    if (this._top) this._top.resize()

    //GAME画面リサイズ
    this._game.resize()
  }

  // ---------------------------------------------------------------------------------------------------------
}

new App()
